import { Box, HStack, Table, Text, VStack } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { MdCheckCircle, MdError, MdWarning } from "react-icons/md";
import { CustomerImportRowResult } from "services/customerImportService";
import { getThemeColor } from "theme/colors";

interface SummaryProps {
  label: string;
  value: number;
  intent?: "success" | "info" | "error";
}

export const Summary: React.FC<SummaryProps> = ({ label, value, intent }) => (
  <VStack
    align="flex-start"
    gap={0}
    minW="120px"
    p={3}
    borderWidth="1px"
    borderRadius="md"
    borderColor={getThemeColor("card.border")}
  >
    <Text
      fontSize="2xl"
      fontWeight="bold"
      color={
        intent === "error"
          ? "red.500"
          : intent === "success"
            ? "green.500"
            : intent === "info"
              ? "blue.500"
              : undefined
      }
    >
      {value}
    </Text>
    <Text fontSize="xs" color="var(--text-secondary-color)">
      {label}
    </Text>
  </VStack>
);

interface RowsTableProps {
  rows: CustomerImportRowResult[];
}

export const RowsTable: React.FC<RowsTableProps> = ({ rows }) => {
  const { t } = useTranslation();
  if (rows.length === 0) {
    return (
      <Text fontSize="sm" color="var(--text-secondary-color)">
        {t("app.customerImport.noRows", { defaultValue: "No rows." })}
      </Text>
    );
  }
  return (
    <Box overflowX="auto" maxH="400px">
      <Table.Root size="sm">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>#</Table.ColumnHeader>
            <Table.ColumnHeader>CustomerId</Table.ColumnHeader>
            <Table.ColumnHeader>Action</Table.ColumnHeader>
            <Table.ColumnHeader>CustomerCompany</Table.ColumnHeader>
            <Table.ColumnHeader>Messages</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map((r) => (
            <Table.Row key={r.rowNumber}>
              <Table.Cell>{r.rowNumber}</Table.Cell>
              <Table.Cell fontFamily="mono" fontSize="xs">
                {r.customerId}
              </Table.Cell>
              <Table.Cell>
                {r.errors.length > 0 ? (
                  <HStack gap={1} color="red.500">
                    <MdError />
                    <Text fontSize="xs">Error</Text>
                  </HStack>
                ) : r.warnings.length > 0 ? (
                  <HStack gap={1} color="orange.500">
                    <MdWarning />
                    <Text fontSize="xs">{r.action}</Text>
                  </HStack>
                ) : (
                  <HStack gap={1} color="green.500">
                    <MdCheckCircle />
                    <Text fontSize="xs">{r.action}</Text>
                  </HStack>
                )}
              </Table.Cell>
              <Table.Cell fontSize="xs">
                {r.resolvedCustomerCompanyName ?? "—"}
              </Table.Cell>
              <Table.Cell fontSize="xs">
                {[
                  ...r.errors.map((e) => `❌ ${e}`),
                  ...r.warnings.map((w) => `⚠️ ${w}`),
                ].join(" · ") || "—"}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
};
